<template>
  <div class="admin-subscription px-2">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="text-white mb-0">订阅模块定价管理</h4>
      <div class="d-flex align-items-center bg-dark p-2 rounded border border-secondary">
        <div class="d-flex align-items-center mr-4 border-right border-secondary pr-4">
          <span class="text-white mr-3">分享奖励积分:</span>
          <b-form-input 
            v-model="referralRewardPoints" 
            type="number" 
            size="sm" 
            style="width: 80px;"
            @blur="updateSettings"
            @keyup.enter="updateSettings"
          ></b-form-input>
        </div>
        <span class="text-white mr-3">每日名额限制:</span>
        <b-form-checkbox v-model="isDailyLimitReached" switch @change="updateSettings">
          <span :class="isDailyLimitReached ? 'text-danger' : 'text-success'">
            {{ isDailyLimitReached ? '名额已满' : '名额充足' }}
          </span>
        </b-form-checkbox>
      </div>
    </div>
    
    <b-tabs content-class="mt-3" active-nav-item-class="font-weight-bold text-primary">
      <!-- 模块管理 -->
      <b-tab title="模块定价管理" active>
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="light"></b-spinner>
        </div>
        
        <div v-else class="table-responsive">
      <table class="table table-dark table-hover border rounded">
        <thead>
          <tr>
            <th>ID</th>
            <th>模块名称</th>
            <th>模块代码 (Code)</th>
            <th>单月原价 (¥)</th>
            <th>状态</th>
            <th>描述</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="mod in modules" :key="mod.id">
            <td>{{ mod.id }}</td>
            <td>{{ mod.name }}</td>
            <td><code>{{ mod.code }}</code></td>
            <td>
              <b-form-input 
                v-if="editingId === mod.id" 
                v-model="editForm.price_per_month" 
                type="number" 
                size="sm" 
                style="width: 100px;" 
                step="0.01"
              ></b-form-input>
              <span v-else>{{ Number(mod.price_per_month).toFixed(2) }}</span>
            </td>
            <td>
              <b-form-checkbox 
                v-if="editingId === mod.id" 
                v-model="editForm.status" 
                :value="1" 
                :unchecked-value="0" 
                switch
              >
                {{ editForm.status === 1 ? '需订阅' : '免费' }}
              </b-form-checkbox>
              <span v-else :class="mod.status === 1 ? 'text-success' : 'text-danger'">
                {{ mod.status === 1 ? '需订阅' : '免费' }}
              </span>
            </td>
            <td>
              <span class="text-muted" style="font-size: 0.85rem;">{{ mod.description }}</span>
            </td>
            <td>
              <template v-if="editingId === mod.id">
                <b-button size="sm" variant="success" class="mr-2" @click="saveModule(mod.id)" :disabled="saving">保存</b-button>
                <b-button size="sm" variant="secondary" @click="cancelEdit">取消</b-button>
              </template>
              <b-button v-else size="sm" variant="outline-primary" @click="startEdit(mod)">编辑</b-button>
            </td>
          </tr>
        </tbody>
        </table>
      </div>
    </b-tab>

    <!-- 优惠券管理 -->
    <b-tab title="优惠券管理">
      <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="text-white mb-0">优惠券生成与管理</h4>
          <b-button variant="primary" @click="$bvModal.show('modal-generate-coupon')">生成优惠券</b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-dark table-hover border rounded">
            <thead>
              <tr>
                <th>券码</th>
                <th>面额 (¥)</th>
                <th>状态</th>
                <th>过期时间</th>
                <th>使用时间</th>
                <th>关联订单</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="coupon in coupons" :key="coupon.id">
                <td><code>{{ coupon.code }}</code></td>
                <td class="text-success font-weight-bold">{{ Number(coupon.amount).toFixed(2) }}</td>
                <td>
                  <b-badge :variant="coupon.status === 1 ? 'success' : 'secondary'">
                    {{ coupon.status === 1 ? '可用' : '已失效' }}
                  </b-badge>
                </td>
                <td>{{ coupon.expire_at }}</td>
                <td>{{ coupon.used_at || '-' }}</td>
                <td>{{ coupon.used_by_order || '-' }}</td>
              </tr>
              <tr v-if="coupons.length === 0">
                <td colspan="6" class="text-center py-4 text-muted">暂无优惠券记录</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-tab>

    <!-- 用户订阅列表 -->
    <b-tab title="用户订阅列表">
      <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="text-white mb-0">用户订阅状态一览</h4>
          <b-button variant="outline-light" size="sm" @click="fetchUserList">刷新列表</b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-dark table-hover border rounded">
            <thead>
              <tr>
                <th>用户</th>
                <th>订阅内容</th>
                <th @click="toggleSort('start_time')" style="cursor: pointer;" title="点击排序">
                  开始时间
                  <b-icon v-if="sortBy === 'start_time'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </th>
                <th @click="toggleSort('end_time')" style="cursor: pointer;" title="点击排序">
                  结束时间
                  <b-icon v-if="sortBy === 'end_time'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </th>
                <th @click="toggleSort('created_at')" style="cursor: pointer;" title="点击排序">
                  创建时间
                  <b-icon v-if="sortBy === 'created_at'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </th>
                <th @click="toggleSort('updated_at')" style="cursor: pointer;" title="点击排序">
                  更新时间
                  <b-icon v-if="sortBy === 'updated_at'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sub in userList" :key="sub.id">
                <td>
                  <div class="text-white">{{ sub.user_nick }}</div>
                  <small class="text-muted">@{{ sub.user_name }} (UID: {{ sub.user_id }})</small>
                </td>
                <td>
                  <b-badge variant="info">{{ sub.module_name }}</b-badge>
                  <div class="mt-1"><small class="text-muted"><code>{{ sub.module_code }}</code></small></div>
                </td>
                <td><small>{{ sub.start_time }}</small></td>
                <td>
                  <div :class="isExpired(sub.end_time) ? 'text-danger' : 'text-success'">
                    {{ sub.end_time }}
                  </div>
                </td>
                <td><small class="text-muted">{{ sub.created_at || '-' }}</small></td>
                <td><small class="text-muted">{{ sub.updated_at || '-' }}</small></td>
                <td>
                  <b-badge :variant="sub.status === 1 ? 'success' : 'secondary'">
                    {{ sub.status === 1 ? '有效' : '禁用' }}
                  </b-badge>
                </td>
              </tr>
              <tr v-if="userList.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">暂无订阅记录</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalRows > perPage" class="mt-4 d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </b-tab>
  </b-tabs>

    <!-- 生成优惠券弹窗 -->
    <b-modal id="modal-generate-coupon" title="批量生成优惠券" header-bg-variant="dark" header-text-variant="light" body-bg-variant="dark" body-text-variant="light" footer-bg-variant="dark" footer-text-variant="light" @ok="handleGenerateCoupons">
      <b-form>
        <b-form-group label="抵扣面额 (¥)" label-for="coupon-amount">
          <b-form-input id="coupon-amount" v-model="genForm.amount" type="number" placeholder="输入抵扣金额"></b-form-input>
        </b-form-group>
        <b-form-group label="生成数量" label-for="coupon-count">
          <b-form-input id="coupon-count" v-model="genForm.count" type="number" placeholder="输入生成个数"></b-form-input>
        </b-form-group>
        <b-form-group label="券码前缀" label-for="coupon-prefix">
          <b-form-input id="coupon-prefix" v-model="genForm.prefix" placeholder="例如: OFF, VIP"></b-form-input>
        </b-form-group>
        <b-form-group label="有效期 (天)" label-for="coupon-days">
          <b-form-input id="coupon-days" v-model="genForm.expire_days" type="number" placeholder="输入过期天数"></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'AdminSubscription',
  data() {
    return {
      modules: [],
      loading: true,
      saving: false,
      editingId: null,
      editForm: {
        price_per_month: 0,
        status: 1
      },
      coupons: [],
      genForm: {
        amount: 100,
        count: 1,
        prefix: 'OFF',
        expire_days: 30
      },
      isDailyLimitReached: false,
      referralRewardPoints: 5,
      userList: [],
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      sortBy: 'end_time',
      sortOrder: 'desc'
    }
  },
  created() {
    this.fetchModules()
    this.fetchCoupons()
    this.fetchSettings()
    this.fetchUserList()
  },
  methods: {
    fetchModules() {
      this.loading = true
      request_zzquant({
        url: '/v3/admin/subscription/modules',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.modules = res.data.data || []
        }
      })
      .catch(() => {
        this.$bvToast.toast('加载模块失败', { variant: 'danger', solid: true })
      })
      .finally(() => {
        this.loading = false
      })
    },
    startEdit(mod) {
      this.editingId = mod.id
      this.editForm = {
        price_per_month: Number(mod.price_per_month),
        status: mod.status
      }
    },
    cancelEdit() {
      this.editingId = null
    },
    saveModule(id) {
      this.saving = true
      request_zzquant({
        url: `/v3/admin/subscription/modules/${id}`,
        method: 'PUT',
        data: {
          price_per_month: Number(this.editForm.price_per_month),
          status: this.editForm.status
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast('保存成功', { variant: 'success', solid: true })
          this.editingId = null
          this.fetchModules()
        } else {
          this.$bvToast.toast((res.data && res.data.message) || '保存失败', { variant: 'danger', solid: true })
        }
      })
      .catch(() => {
        this.$bvToast.toast('保存请求失败', { variant: 'danger', solid: true })
      })
      .finally(() => {
        this.saving = false
      })
    },
    fetchCoupons() {
      request_zzquant({
        url: '/v3/admin/subscription/coupons',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.coupons = res.data.data || []
        }
      })
    },
    handleGenerateCoupons(bvModalEvt) {
      // 防止弹窗自动关闭，等待异步完成
      bvModalEvt.preventDefault()
      
      if (!this.genForm.amount || !this.genForm.count) {
        this.$bvToast.toast('请填写金额和数量', { variant: 'warning', solid: true })
        return
      }

      request_zzquant({
        url: '/v3/admin/subscription/coupons/generate',
        method: 'POST',
        data: {
          amount: Number(this.genForm.amount),
          count: Number(this.genForm.count),
          prefix: this.genForm.prefix,
          expire_days: Number(this.genForm.expire_days)
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast(`成功生成 ${res.data.data.length} 张优惠券`, { variant: 'success', solid: true })
          this.$bvModal.hide('modal-generate-coupon')
          this.fetchCoupons()
        } else {
          this.$bvToast.toast(res.data.message || '生成失败', { variant: 'danger', solid: true })
        }
      })
      .catch(() => {
        this.$bvToast.toast('生成请求失败', { variant: 'danger', solid: true })
      })
    },
    fetchSettings() {
      request_zzquant({
        url: '/v3/admin/subscription/settings',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.isDailyLimitReached = res.data.data.is_daily_limit_reached
          this.referralRewardPoints = res.data.data.referral_reward_points
        }
      })
    },
    updateSettings() {
      request_zzquant({
        url: '/v3/admin/subscription/settings',
        method: 'POST',
        data: {
          is_daily_limit_reached: this.isDailyLimitReached,
          referral_reward_points: Number(this.referralRewardPoints)
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const msg = this.isDailyLimitReached ? '已开启名额限制' : '已解除名额限制'
          this.$bvToast.toast(msg, { variant: 'success', solid: true })
        } else {
          this.$bvToast.toast('更新设置失败', { variant: 'danger', solid: true })
          // 恢复原状
          this.isDailyLimitReached = !this.isDailyLimitReached
        }
      })
      .catch(() => {
        this.$bvToast.toast('网络请求失败', { variant: 'danger', solid: true })
        this.isDailyLimitReached = !this.isDailyLimitReached
      })
    },
    fetchUserList() {
      request_zzquant({
        url: '/v3/admin/subscription/user-list',
        method: 'GET',
        params: {
          page: this.currentPage,
          page_size: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const { items, total } = res.data.data
          this.userList = items || []
          this.totalRows = total || 0
        }
      })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.$nextTick(() => {
        this.fetchUserList()
      })
    },
    toggleSort(key) {
      if (this.sortBy === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortOrder = 'desc'
      }
      this.currentPage = 1
      this.fetchUserList()
    },
    isExpired(endTime) {
      if (!endTime || endTime === '-') return false
      return new Date(endTime) < new Date()
    }
  }
}
</script>
